import { Component, DestroyRef, effect, inject } from '@angular/core';
import { AlertFormComponent } from './alert-form/alert-form.component';
import { AssetVersionPipe } from '../../pipes/asset-version.pipe';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToggleSwitchComponent } from '../../shared/components/toggle-switch/toggle-switch.component';
import {
  AlertTypeMap,
  AlertSubscriptionsListResponse,
  AlertSubscriptionList,
  AlertSubscriptionResponse,
  EmptyAlertSubscriptionResponse,
  AlertType,
  AlertSubscriptionPayload,
  PosthogEventNames, PosthogEventProperties
} from './types';
import { AlertsService } from '../../services/alerts.service';
import { LoaderComponent } from '../../shared/components/loader/loader.component';
import { StateService } from '../../services/state.service';
import { DashboardService, MyBrandAndCompetitors } from '../../services/dashboard.service';
import { UtilService } from '../../services/util.service';
import {PosthogService} from '../../services/posthog.service';
import {AlertUtils} from './utils';

@Component({
  selector: 'app-alert-center',
  imports: [
    AlertFormComponent,
    AssetVersionPipe,
    CommonModule,
    ToggleSwitchComponent,
    LoaderComponent
  ],
  templateUrl: './alert-center.component.html',
  styleUrl: './alert-center.component.scss'
})
export class AlertCenterComponent {

  private utilService = inject(UtilService);
  private dashboardService = inject(DashboardService);
  private alertsService = inject(AlertsService);
  private destroyRef = inject(DestroyRef);
  private stateService = inject(StateService);
  private posthogService = inject(PosthogService);

  protected myBrandsAndCompetitorsSignal = this.dashboardService.myBrandsAndCompetitors;
  protected myBrandsAndCompetitors: MyBrandAndCompetitors[] = [];

  protected alertTypeMap = AlertTypeMap;
  protected loading: boolean = false;
  protected alertSubscriptions: AlertSubscriptionList | null = null;
  protected selectedAlert: AlertSubscriptionResponse | EmptyAlertSubscriptionResponse | null = null;

  protected isChildRouteActive = false;

  constructor() {
    effect(() => {
      this.onAccountChange();
    });
  }

  ngOnInit(): void {
    this.getAlertSubscriptions();
  }

  private onAccountChange(): void {
    this.myBrandsAndCompetitors = this.myBrandsAndCompetitorsSignal() || [];
    this.selectedAlert = null;
    this.getAlertSubscriptions();
  }

  private getAlertSubscriptions(): void {
    this.loading = true;
    const accountId = this.stateService.activeAccount();
    this.alertsService.getAlertSubscriptions(accountId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.alertSubscriptions = this.getAlertSubscription(response);
          this.loading = false;
        },
        error: (error) => {
          console.error('Error fetching alert subscriptions:', error);
          this.loading = false;
        }
      });
  }

  private getAlertSubscription(response?: AlertSubscriptionsListResponse): AlertSubscriptionList {
    if (response) {
      // fill spike/drop alert placeholders in case they are not configured
      let alertSubscriptions: AlertSubscriptionList = { alerts: response.alerts };
      const existingTypes = response.alerts.map(alert => alert.alertType);

      Object.values(AlertType).forEach(type => {
        if (!existingTypes.includes(type)) {
          alertSubscriptions.alerts.push({
            subscriptionId: undefined,
            alertType: type,
            metric: undefined,
            brands: [],
            frequency: undefined,
            enabled: false
          });
        }
      });

      const alertOrder = Object.values(AlertType);
      alertSubscriptions.alerts.sort((a, b) => alertOrder.indexOf(a.alertType) - alertOrder.indexOf(b.alertType));

      return alertSubscriptions;
    }

    // no response status 204 - no alert subscriptions
    return this.getEmptyAlertSubscriptions();
  }

  private getEmptyAlertSubscriptions(): AlertSubscriptionList {
    return {
      alerts: [
        {
          subscriptionId: undefined,
          alertType: AlertType.SPIKE,
          metric: undefined,
          brands: [],
          frequency: undefined,
          enabled: false
        },
        {
          subscriptionId: undefined,
          alertType: AlertType.DROP,
          metric: undefined,
          brands: [],
          frequency: undefined,
          enabled: false
        }
      ]
    };
  }

  protected toggleAlert(alert: AlertSubscriptionResponse | EmptyAlertSubscriptionResponse, isActivated: boolean): void {
    if (alert.subscriptionId) {
      const payload: AlertSubscriptionPayload = {
        subscriptionId: alert.subscriptionId,
        accountId: this.stateService.activeAccount(),
        alertType: alert.alertType,
        metric: alert.metric!,
        brands: alert.brands,
        frequency: alert.frequency!,
        enabled: !alert.enabled,
      };
      this.alertsService.updateAlertSubscription(payload)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            const alertTypeForPosthog = AlertUtils.alertTypeToPosthogProperty(alert.alertType);
            if (alertTypeForPosthog){
              const posthogEventName = isActivated ? PosthogEventNames.ALERT_ACTIVATED : PosthogEventNames.ALERT_DEACTIVATED;
              this.posthogService.capture(posthogEventName, {
                [PosthogEventProperties.ALERT_TYPE]:AlertUtils.alertTypeToPosthogProperty(alert.alertType),
              });
            }
            this.utilService.openSnackBar('Updated successfully.');
          },
          error: (error) => {
            this.utilService.openSnackBar('Error occurred. Please try saving again.');
            console.error('Error creating/updating alert subscription:', error);
          }
        });
    } else {
      this.openAlertForm(alert);
    }
  }

  protected openAlertForm(alert: AlertSubscriptionResponse | EmptyAlertSubscriptionResponse): void {
    this.selectedAlert = alert;
  }

  protected closeAlertForm(isUpdated: boolean): void {
    if (isUpdated) {
      this.utilService.openSnackBar('Updated successfully.');
      this.getAlertSubscriptions();
    }
    this.selectedAlert = null;
  }

}
